import { Funnel } from '@fullstory/proto/fs/services/search-settings';
import { SearchGroup } from '@fullstory/search-model';
import { EventType } from '@fullstory/search-types';
import { WithID } from '@fullstory/util/uid';
import {
  ComputeFunnelMetricsRequest,
  ComputeFunnelMetricsResponse,
  QueryCacheOptions,
  SampleOptions,
} from '@fullstory/proto/fs/services/alexa';
import { FunnelDetail } from '@fullstory/proto/fs/services/dx-objects';
import { AppCacheStatus } from '@fullstory/redux-util/utils/';
import {
  FunnelDataRow,
  FunnelDataTables,
  FunnelDataTable,
} from '@fullstory/proto/fs/api/metrics/funnelmetrics';

export enum FunnelCreateTypes {
  ShowModal = 'Funnels.CreateModal.ShowModal',
  ResetModal = 'Funnels.CreateModal.Reset',
  SetSelected = 'Funnels.CreateModal.SetSelected',
  SetFunnelData = 'Funnels.CreateModal.SetFunnelData',
  FetchFunnelDataFailed = 'Funnels.CreateModal.FetchFunnelDataFailed',
  FetchFunnelData = 'Funnels.CreateModal.FetchFunnelData',
}

export type FunnelDetails = {
  count: number;
  label: string;
};

export type FunnelCounts = {
  count: number;
  step: WithID<EventType>;
};

export interface FunnelCreate extends Funnel {
  eventModel: SearchGroup<EventType>[];
}

export enum FunnelsRequestTypes {
  GetFunnelRequest = 'funnels/GET_FUNNEL_REQUEST',
  GetFunnelSuccess = 'funnels/GET_FUNNEL_SUCCESS',
  GetFunnelFailure = 'funnels/GET_FUNNEL_FAILURE',
  GetFunnelDetailViewRequest = 'funnels/GET_FUNNEL_DETAIL_VIEW_REQUEST',
  GetFunnelDetailViewSuccess = 'funnels/GET_FUNNEL_DETAIL_VIEW_SUCCESS',
  GetFunnelDetailViewFailure = 'funnels/GET_FUNNEL_DETAIL_VIEW_FAILURE',
  GetAllFunnelsRequest = 'funnels/GET_ALL_FUNNELS_REQUEST',
  GetAllFunnelsSuccess = 'funnels/GET_ALL_FUNNELS_SUCCESS',
  GetAllFunnelsFailure = 'funnels/GET_ALL_FUNNELS_FAILURE',
  SetFunnelRequest = 'funnels/SET_FUNNEL_REQUEST',
  SetFunnelSuccess = 'funnels/SET_FUNNEL_SUCCESS',
  SetFunnelFailure = 'funnels/SET_FUNNEL_FAILURE',
  MarkViewedRequest = 'funnels/MARK_VIEWED_REQUEST',
  MarkViewedSuccess = 'funnels/MARK_VIEWED_SUCCESS',
  MarkViewedFailure = 'funnels/MARK_VIEWED_FAILURE',
  StarFunnelRequest = 'funnels/STAR_FUNNEL_REQUEST',
  StarFunnelSuccess = 'funnels/STAR_FUNNEL_SUCCESS',
  StarFunnelFailure = 'funnels/STAR_FUNNEL_FAILURE',
  ArchiveFunnelRequest = 'funnels/ARCHIVE_FUNNEL_REQUEST',
  ArchiveFunnelSuccess = 'funnels/ARCHIVE_FUNNEL_SUCCESS',
  ArchiveFunnelFailure = 'funnels/ARCHIVE_FUNNEL_FAILURE',
  ComputeFunnelMetricsRequest = 'funnels/COMPUTE_FUNNEL_METRICS_REQUEST',
  ComputeFunnelMetricsSuccess = 'funnels/COMPUTE_FUNNEL_METRICS_SUCCESS',
  ComputeFunnelMetricsFailure = 'funnels/COMPUTE_FUNNEL_METRICS_FAILURE',
  CreateUnnamedFunnelRequest = 'funnels/CREATE_UNNAMED_FUNNEL_REQUEST',
  CreateUnnamedFunnelSuccess = 'funnels/CREATE_UNNAMED_FUNNEL_SUCCESS',
  CreateUnnamedFunnelFailure = 'funnels/CREATE_UNNAMED_FUNNEL_FAILURE',
  GetUnnamedFunnelRequest = 'funnels/GET_UNNAMED_FUNNEL_REQUEST',
  GetUnnamedFunnelSuccess = 'funnels/GET_UNNAMED_FUNNEL_SUCCESS',
  GetUnnamedFunnelFailure = 'funnels/GET_UNNAMED_FUNNEL_FAILURE',
  GetFunnelsAccessRequest = 'funnels/GET_ACCESS_REQUEST',
  GetFunnelsAccessSuccess = 'funnels/GET_ACCESS_SUCCESS',
  GetFunnelsAccessFailure = 'funnels/GET_ACCESS_FAILURE',
  FunnelAppCacheIsLoading = 'funnels/APP_CACHE_IS_LOADING',
  FunnelAppCacheLoadFailed = 'funnels/APP_CACHE_LOAD_FAILED',
  FunnelAppCacheRefetching = 'funnels/APP_CACHE_IS_REFETCHING',
  FunnelAppCacheHasData = 'funnels/APP_CACHE_HAS_DATA',
  FunnelAppCacheFresh = 'funnels/APP_CACHE_FRESH',
  FunnelAppCacheSwap = 'funnels/APP_CACHE_SWAP',
  FunnelAppCacheClear = 'funnels/APP_CACHE_CLEAR',
}

export type FunnelsById = { [id: string]: FunnelDetail };
export type FunnelsState = FunnelsById;
export type FunnelMetricsDataType = {
  funnelMetrics: ComputeFunnelMetricsResponseWithId;
  totalRevenue?: number;
  averageOrderValue?: number;
};

export interface FunnelMetricsResponse extends FunnelMetricsDataType {
  status: AppCacheStatus;
  safeMessage: string | null;
  segmentId: string;
  isPast: boolean;
}

export interface FunnelMetricsRequestBody
  extends Omit<ComputeFunnelMetricsRequest, 'now' | 'timezone' | 'cacheOptions' | 'sampleOptions'> {
  cacheOptions?: QueryCacheOptions;
  sampleOptions?: SampleOptions;
}

export interface GraphicInfoType extends FunnelDetails {
  step: number;
}

export interface FunnelValues {
  userCount: number;
  conversionRate: number;
  medianTimeFromFirst: number;
  label: string;
}

export interface FunnelRevenueValues {
  totalRevenue: number | null;
  averageOrderValue: number | null;
}

export interface FunnelSummaryValues extends FunnelRevenueValues, FunnelValues {}

export interface FunnelDataRowWithMetadata extends FunnelDataRow {
  isPast: boolean;
  isSegment: boolean;
}

export interface FunnelDataTableWithId<R extends FunnelDataRow> extends WithID<FunnelDataTable> {
  table: WithID<R>[];
}

export interface FunnelDataTablesWithId<R extends FunnelDataRow> extends FunnelDataTables {
  steps: FunnelDataTableWithId<R>[];
}
export interface ComputeFunnelMetricsResponseWithId extends ComputeFunnelMetricsResponse {
  data: FunnelDataTablesWithId<FunnelDataRow> | null;
}
